/**
 * API types. Mirrors the server's domain types (server/src/types/domain.ts) as serialised to JSON.
 */
export const SEVERITIES = ['critical', 'high', 'medium', 'low', 'info'] as const;
export type Severity = (typeof SEVERITIES)[number];

export const CATEGORIES = ['bug', 'security', 'performance', 'error-handling', 'maintainability', 'reliability', 'testing'] as const;
export type Category = (typeof CATEGORIES)[number];

export const FINDING_STATUSES = ['open', 'accepted', 'dismissed', 'resolved'] as const;
export type FindingStatus = (typeof FINDING_STATUSES)[number];

export type ReviewStatus = 'queued' | 'processing' | 'completed' | 'failed';
export type ReviewStage = 'queued' | 'fetching_files' | 'preparing_context' | 'analyzing' | 'validating' | 'saving' | 'done';
export type ReviewDepth = 'quick' | 'thorough';
export type FileSkipReason =
  | 'excluded-pattern'
  | 'binary'
  | 'no-patch'
  | 'patch-too-large'
  | 'total-budget-exceeded'
  | 'max-files-exceeded';

export type SeverityCounts = Record<Severity, number>;

export interface ChangedFile {
  path: string;
  previousPath?: string;
  changeType: string;
  additions: number;
  deletions: number;
  patch?: string;
  patchTruncated: boolean;
  analyzed: boolean;
  skipReason?: FileSkipReason;
}

export interface SafeError {
  code: string;
  message: string;
  retryable: boolean;
}

export interface ReviewSummary {
  id: string;
  owner: string;
  repo: string;
  prNumber: number;
  prUrl: string;
  prTitle: string;
  prAuthor: string;
  sourceBranch: string;
  targetBranch: string;
  headSha: string;
  prState: 'open' | 'closed' | 'merged';
  additions: number;
  deletions: number;
  changedFileCount: number;
  status: ReviewStatus;
  stage: ReviewStage;
  source: 'claude' | 'demo';
  trigger: 'manual' | 'webhook';
  settings: { depth: ReviewDepth; focus?: string };
  summary?: string;
  findingCounts: SeverityCounts;
  findingTotal: number;
  discardedFindingCount: number;
  fileListTruncated: boolean;
  modelId?: string;
  usage?: { inputTokens?: number; outputTokens?: number; latencyMs?: number; attempts?: number; estimatedCostUsd?: number };
  error?: SafeError;
  publishedCommentCount: number;
  startedAt?: string;
  completedAt?: string;
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Review extends ReviewSummary {
  files: ChangedFile[];
}

export interface Finding {
  id: string;
  reviewId: string;
  title: string;
  severity: Severity;
  category: Category;
  filePath: string;
  line?: number;
  locationCheck: 'verified-line' | 'file-only';
  explanation: string;
  evidence: string;
  impact: string;
  suggestedFix: string;
  confidence?: number;
  needsContext: boolean;
  status: FindingStatus;
  dismissReason?: string;
  publishedAt?: string;
  publishedCommentUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface DashboardStats {
  totalReviews: number;
  byStatus: Record<ReviewStatus, number>;
  findingsBySeverity: SeverityCounts;
  totalFindings: number;
  demoReviews: number;
  recent: ReviewSummary[];
}

export interface PullRequestSummary {
  owner: string;
  repo: string;
  prNumber: number;
  prUrl: string;
  prTitle: string;
  prAuthor: string;
  reviewCount: number;
  latestReview: ReviewSummary;
}

export interface AppConfigStatus {
  demoMode: boolean;
  storage: { kind: 'mongo' | 'memory'; persistent: boolean; retentionDays: number };
  claude: {
    configured: boolean;
    apiKeyPresent: boolean;
    apiKeyHint: string | null;
    model: string | null;
    timeoutMs: number;
    maxRetries: number;
    maxOutputTokens: { quick: number; thorough: number };
  };
  github: { mode: 'demo' | 'live'; tokenConfigured: boolean; tokenHint: string | null; canPublish: boolean };
  webhook: { enabled: boolean; allowlistCount: number };
  credits: { signupFreeCredits: number; costPerReview: number };
  limits: { maxFiles: number; maxPatchBytesPerFile: number; maxTotalDiffBytes: number };
  samplePullRequests: Array<{ url: string; title: string }>;
}

export interface User {
  id: string;
  name: string;
  email: string;
  credits: number;
  createdAt: string;
}

export interface PublicInfo {
  signupFreeCredits: number;
  demoMode: boolean;
}

export interface CommentPreview {
  findingId: string;
  title: string;
  path: string;
  line?: number;
  inline: boolean;
  body: string;
}

export interface PublishResult {
  reviewUrl?: string;
  simulated: boolean;
  inlineCount: number;
  foldedCount: number;
  publishedCount: number;
}

export interface ReviewListParams {
  page: number;
  pageSize: number;
  search?: string;
  status?: ReviewStatus;
  sort: 'createdAt' | 'findingTotal' | 'repo';
  order: 'asc' | 'desc';
}
